import { useEffect, useRef, useState } from "react";
import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import type { JobCategoryWithTags } from "@/types/db";
import type { LocationsResponse } from "@/lib/locations";
import { CATEGORY_ORDER, CATEGORY_LABELS } from "@/lib/categoryLabels";
import TagPill from "@/components/common/tagPill";

interface HeaderProps {
  jobCategoriesWithTags: JobCategoryWithTags[];
  locations: LocationsResponse;
}

interface OrderedCategory {
  slug: string;
  name: string;
  category: JobCategoryWithTags;
}

const navLinkBase =
  "rounded-md px-3 py-2 text-sm font-medium transition-colors lg:text-base";
const navLinkInactive = "text-gray-600 hover:text-gray-900";
const navLinkActive = "text-gray-900 font-semibold";

const primaryCtaBase =
  "bg-polaris-orange rounded-full px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400 sm:px-4 lg:text-base lg:px-5";

const MOBILE_NAV_DRAWER_ID = "mobile-nav-drawer";
const BROWSE_MENU_ID = "site-browse-menu";

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    className={`size-4 transition-transform duration-150 ${open ? "rotate-180" : ""}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

export default function Header({
  jobCategoriesWithTags,
  locations,
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isSearchActive = pathname === "/search";

  const mobileTriggerRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const restoreFocusRafRef = useRef<number | null>(null);

  const browseButtonRef = useRef<HTMLButtonElement | null>(null);
  const browsePanelRef = useRef<HTMLDivElement | null>(null);

  // Close both menus on route change.
  useEffect(() => {
    return router.subscribe("onResolved", () => {
      setIsMobileMenuOpen(false);
      setIsBrowseOpen(false);
    });
  }, [router]);

  // Escape closes whichever menu is open.
  useEffect(() => {
    if (!isMobileMenuOpen && !isBrowseOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isBrowseOpen) {
        setIsBrowseOpen(false);
        browseButtonRef.current?.focus();
      }
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobileMenuOpen, isBrowseOpen]);

  // Outside-click closes the browse panel.
  useEffect(() => {
    if (!isBrowseOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !browsePanelRef.current?.contains(t) &&
        !browseButtonRef.current?.contains(t)
      ) {
        setIsBrowseOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isBrowseOpen]);

  // Mobile drawer focus management.
  useEffect(() => {
    if (isMobileMenuOpen) {
      if (restoreFocusRafRef.current !== null) {
        cancelAnimationFrame(restoreFocusRafRef.current);
        restoreFocusRafRef.current = null;
      }
      drawerRef.current?.querySelector<HTMLElement>("a, button")?.focus();
      return;
    }
    const active = document.activeElement;
    const focusWasInside =
      active instanceof Node && drawerRef.current?.contains(active);
    if (!focusWasInside) return;
    restoreFocusRafRef.current = requestAnimationFrame(() => {
      restoreFocusRafRef.current = null;
      mobileTriggerRef.current?.focus();
    });
  }, [isMobileMenuOpen]);

  // Build ordered categories.
  const categoriesBySlug: Record<string, JobCategoryWithTags> =
    Object.fromEntries(jobCategoriesWithTags.map((c) => [c.slug, c]));
  const orderedCategories: OrderedCategory[] = CATEGORY_ORDER.map((slug) => ({
    slug,
    name: CATEGORY_LABELS[slug] ?? categoriesBySlug[slug]?.name ?? slug,
    category: categoriesBySlug[slug],
  })).filter((c): c is OrderedCategory => Boolean(c.category?.jobTags?.length));

  return (
    <header className="relative">
      {/* Mobile bar */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:hidden">
        <Link
          to="/"
          className="text-polaris-orange flex-none text-lg font-semibold tracking-tight"
        >
          Data First Jobs
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/post-job" className={primaryCtaBase}>
            Post a Job
          </Link>
          <button
            ref={mobileTriggerRef}
            type="button"
            className="relative flex size-10 items-center justify-center rounded-md text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls={MOBILE_NAV_DRAWER_ID}
          >
            <span
              className={`absolute h-0.5 w-6 rounded-full bg-gray-600 transition-transform duration-200 ${
                isMobileMenuOpen ? "rotate-45" : "-translate-y-1.5"
              }`}
              aria-hidden="true"
            />
            <span
              className={`absolute h-0.5 w-6 rounded-full bg-gray-600 transition-opacity duration-200 ${
                isMobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
              aria-hidden="true"
            />
            <span
              className={`absolute h-0.5 w-6 rounded-full bg-gray-600 transition-transform duration-200 ${
                isMobileMenuOpen ? "-rotate-45" : "translate-y-1.5"
              }`}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Mobile drawer — surface the same taxonomy as the desktop mega-menu. */}
      <div
        ref={drawerRef}
        id={MOBILE_NAV_DRAWER_ID}
        inert={!isMobileMenuOpen}
        aria-hidden={!isMobileMenuOpen}
        className={`mx-4 mb-4 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5 transition-all duration-300 sm:hidden ${
          isMobileMenuOpen
            ? "max-h-[calc(100dvh-6rem)] overflow-y-auto opacity-100"
            : "pointer-events-none max-h-0 opacity-0"
        }`}
      >
        <ul className="flex flex-col">
          <li>
            <Link
              to="/search"
              aria-current={isSearchActive ? "page" : undefined}
              className={`block border-b border-gray-100 px-4 py-3 text-base transition-colors hover:bg-gray-50 ${
                isSearchActive ? "font-semibold text-gray-900" : "text-gray-700"
              }`}
            >
              Search Jobs
            </Link>
          </li>
          {orderedCategories.map((c) => (
            <li key={c.slug} className="border-b border-gray-100 px-4 py-3">
              <Link
                to="/browse/$slug"
                params={{ slug: c.slug }}
                className="flex items-center justify-between text-base font-semibold text-gray-900 hover:text-gray-700"
              >
                {c.name}
                <span className="text-xs font-medium text-gray-400 tabular-nums">
                  {c.category.jobTags.length}
                </span>
              </Link>
              <ul className="mt-2 flex flex-wrap gap-1.5 text-xs" role="list">
                {c.category.jobTags.slice(0, 5).map((tag) => (
                  <li key={tag.id}>
                    <Link
                      to="/$slug"
                      params={{ slug: tag.slug }}
                      className="rounded-full bg-gray-100 px-2.5 py-1 text-gray-700 hover:bg-gray-200"
                    >
                      {tag.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>

      {/* Desktop nav */}
      <nav className="mx-auto hidden max-w-7xl items-center justify-between px-4 py-5 sm:flex sm:px-6 lg:px-8">
        <Link
          to="/"
          className="text-polaris-orange text-xl font-semibold tracking-tight lg:text-2xl"
        >
          Data First Jobs
        </Link>
        <ul className="flex items-center gap-2 lg:gap-3">
          <li>
            <button
              ref={browseButtonRef}
              type="button"
              onClick={() => setIsBrowseOpen((v) => !v)}
              aria-expanded={isBrowseOpen}
              aria-haspopup="true"
              aria-controls={BROWSE_MENU_ID}
              className={`${navLinkBase} ${navLinkInactive} inline-flex items-center gap-1`}
            >
              Browse
              <ChevronDown open={isBrowseOpen} />
            </button>
          </li>
          <li>
            <Link
              to="/search"
              aria-current={isSearchActive ? "page" : undefined}
              className={`${navLinkBase} ${isSearchActive ? navLinkActive : navLinkInactive}`}
            >
              Search Jobs
            </Link>
          </li>
          <li>
            <Link to="/post-job" className={primaryCtaBase}>
              Post a Job
            </Link>
          </li>
        </ul>
      </nav>

      {/* Desktop mega-menu panel */}
      {isBrowseOpen && (
        <div
          ref={browsePanelRef}
          id={BROWSE_MENU_ID}
          className="absolute inset-x-0 top-full z-50 hidden border-t border-gray-200 bg-white shadow-xl sm:block"
        >
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
              {orderedCategories.map((c) => (
                <div key={c.slug}>
                  <Link
                    to="/browse/$slug"
                    params={{ slug: c.slug }}
                    className="group mb-3 flex items-baseline justify-between"
                  >
                    <h3 className="text-xs font-semibold tracking-wide text-gray-500 uppercase transition-colors group-hover:text-gray-900">
                      {c.name}
                    </h3>
                    <span className="text-xs text-gray-400 tabular-nums">
                      {c.category.jobTags.length}
                    </span>
                  </Link>
                  <ul className="space-y-1.5 text-sm" role="list">
                    {c.category.jobTags.slice(0, 6).map((tag) => (
                      <li key={tag.id}>
                        <TagPill
                          variant="link"
                          to="/$slug"
                          params={{ slug: tag.slug }}
                        >
                          {tag.name}
                        </TagPill>
                      </li>
                    ))}
                    {c.category.jobTags.length > 6 && (
                      <li>
                        <Link
                          to="/browse/$slug"
                          params={{ slug: c.slug }}
                          className="text-polaris-orange text-sm font-medium transition-colors hover:text-orange-700"
                        >
                          See all {c.name.toLowerCase()} →
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              ))}
              {locations.countries.length > 0 && (
                <div>
                  <div className="mb-3 flex items-baseline justify-between">
                    <h3 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                      Locations
                    </h3>
                    <span className="text-xs text-gray-400 tabular-nums">
                      {locations.countries.length}
                    </span>
                  </div>
                  <ul className="space-y-1.5 text-sm" role="list">
                    {locations.countries.slice(0, 6).map((country) => (
                      <li key={country.slug}>
                        <TagPill
                          variant="link"
                          to="/$slug"
                          params={{ slug: country.slug }}
                        >
                          {country.name}
                        </TagPill>
                      </li>
                    ))}
                    <li>
                      <Link
                        to="/locations"
                        className="text-polaris-orange text-sm font-medium hover:text-orange-700"
                      >
                        See all locations
                      </Link>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
